import { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Chip,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button
} from "@mui/material";
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    PauseCircle as PauseIcon,
    PlayCircle as PlayIcon,
    Schedule as ScheduleIcon,
    Straighten as MeasurementsIcon,
    MonitorWeight as WeightIcon,
    CameraAlt as PhotoIcon,
    Description as FormIcon,
    Close as CloseIcon
} from "@mui/icons-material";
import ScheduledCheckInDialog from "./ScheduledCheckInDialog";
import {
    getScheduledCheckIns,
    getScheduledCheckInById,
    createScheduledCheckIn,
    updateScheduledCheckIn,
    deleteScheduledCheckIn,
    toggleScheduledCheckInActive
} from "../../services/ScheduledCheckInService";
import { useCompany } from "../../contexts/CompanyContext";
import { useMessageContext } from "../../contexts/MessageContext";
import { getErrorMessage } from "../../enums/ErrorMessages";

const FREQUENCY_LABELS = {
    DAILY: "יומי",
    WEEKLY: "שבועי",
    MONTHLY: "חודשי"
};

const DAY_LABELS = {
    SUNDAY: "יום א'",
    MONDAY: "יום ב'",
    TUESDAY: "יום ג'",
    WEDNESDAY: "יום ד'",
    THURSDAY: "יום ה'",
    FRIDAY: "יום ו'",
    SATURDAY: "שבת"
};

const ITEM_TYPE_CONFIG = {
    BODY_MEASUREMENT: { icon: <MeasurementsIcon sx={{ fontSize: "16px" }} />, color: "#8B5CF6", label: "מדידות" },
    BODY_WEIGHT: { icon: <WeightIcon sx={{ fontSize: "16px" }} />, color: "#3B82F6", label: "משקל" },
    PROGRESS_PHOTO: { icon: <PhotoIcon sx={{ fontSize: "16px" }} />, color: "#10B981", label: "תמונות" },
    FORM: { icon: <FormIcon sx={{ fontSize: "16px" }} />, color: "#F59E0B", label: "טופס" }
};

const formatDate = (epochSeconds) => {
    if (!epochSeconds) return "-";
    const date = new Date(epochSeconds * 1000);
    return date.toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const formatScheduleDescription = (schedule) => {
    if (schedule.frequency === "WEEKLY") {
        const dayLabels = (schedule.scheduleDays || []).map(d => DAY_LABELS[d] || d).join(", ");
        const interval = schedule.intervalWeeks && schedule.intervalWeeks > 1
            ? `כל ${schedule.intervalWeeks} שבועות`
            : "שבועי";
        return dayLabels ? `${interval} - ${dayLabels}` : interval;
    }
    if (schedule.frequency === "MONTHLY") {
        const days = (schedule.daysOfMonth || []).sort((a, b) => a - b).join(", ");
        const interval = schedule.intervalMonths && schedule.intervalMonths > 1
            ? `כל ${schedule.intervalMonths} חודשים`
            : "חודשי";
        return days ? `${interval} - יום ${days}` : interval;
    }
    return FREQUENCY_LABELS[schedule.frequency] || schedule.frequency;
};

const ScheduledCheckInList = ({ onCreateClick }) => {
    const { company } = useCompany();
    const { notificationHandler } = useMessageContext();

    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editData, setEditData] = useState(null);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const fetchSchedules = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getScheduledCheckIns(0, 100, company?.id);
            const data = res.data?.content || res.data || [];
            setSchedules(data);
        } catch (e) {
            console.error("Error fetching scheduled check-ins:", e);
            setSchedules([]);
        } finally {
            setLoading(false);
        }
    }, [company?.id]);

    useEffect(() => {
        fetchSchedules();
    }, [fetchSchedules]);

    const handleCreate = () => {
        setEditData(null);
        setDialogOpen(true);
    };

    const handleEdit = async (schedule) => {
        try {
            const res = await getScheduledCheckInById(schedule.id);
            setEditData(res.data);
            setDialogOpen(true);
        } catch (e) {
            notificationHandler.error("שגיאה בטעינת הנתונים");
        }
    };

    const handleSubmit = async (data, editId) => {
        if (editId) {
            await updateScheduledCheckIn(editId, data);
            notificationHandler.success("צ'ק-אין מתוזמן עודכן בהצלחה");
        } else {
            await createScheduledCheckIn(data);
            notificationHandler.success("צ'ק-אין מתוזמן נוצר בהצלחה");
        }
        fetchSchedules();
    };

    const handleToggleActive = async (schedule) => {
        try {
            await toggleScheduledCheckInActive(schedule.id);
            notificationHandler.success(schedule.isActive ? "צ'ק-אין מתוזמן הושהה" : "צ'ק-אין מתוזמן הופעל");
            fetchSchedules();
        } catch (e) {
            notificationHandler.error(getErrorMessage(e.response?.data?.errorCode) || "שגיאה בעדכון הסטטוס");
        }
    };

    const handleDeleteClick = (schedule) => {
        setDeleteTarget(schedule);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (deleteTarget) {
            try {
                await deleteScheduledCheckIn(deleteTarget.id);
                notificationHandler.success("צ'ק-אין מתוזמן נמחק בהצלחה");
                fetchSchedules();
            } catch (e) {
                notificationHandler.error(getErrorMessage(e.response?.data?.errorCode) || "שגיאה במחיקה");
            }
        }
        setDeleteDialogOpen(false);
        setDeleteTarget(null);
    };

    // Expose create handler to parent
    useEffect(() => {
        if (onCreateClick) onCreateClick.current = handleCreate;
    });

    const TABLE_HEADERS = [
        { label: "שם", align: "right", width: "20%" },
        { label: "תדירות", align: "right", width: "20%" },
        { label: "פריטים", align: "center", width: "20%" },
        { label: "נמענים", align: "center", width: "10%" },
        { label: "שליחה הבאה", align: "center", width: "15%" },
        { label: "סטטוס", align: "center", width: "5%" },
        { label: "פעולות", align: "center", width: "10%" }
    ];

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress sx={{ color: "#57837E" }} />
            </Box>
        );
    }

    if (schedules.length === 0) {
        return (
            <>
                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 8,
                    gap: 2
                }}>
                    <ScheduleIcon sx={{ fontSize: "48px", color: "#CBD5E1" }} />
                    <Typography sx={{ fontSize: "16px", fontWeight: 500, color: "#64748B", textAlign: "center" }}>
                        אין צ'ק-אינים מתוזמנים
                    </Typography>
                    <Typography sx={{ fontSize: "14px", color: "#94A3B8", textAlign: "center" }}>
                        צור צ'ק-אין מתוזמן כדי לשלוח צ'ק-אינים אוטומטית למתאמנים שלך
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={handleCreate}
                        sx={{
                            mt: 1,
                            backgroundColor: "#57837E",
                            fontFamily: '"Heebo", "Roboto", sans-serif',
                            "&:hover": { backgroundColor: "#46706B" }
                        }}
                    >
                        צור צ'ק-אין מתוזמן
                    </Button>
                </Box>

                <ScheduledCheckInDialog
                    open={dialogOpen}
                    onClose={() => { setDialogOpen(false); setEditData(null); }}
                    onSubmit={handleSubmit}
                    editData={editData}
                />
            </>
        );
    }

    return (
        <>
            {/* Desktop Table */}
            <Box sx={{ display: { xs: "none", md: "block" }, px: 3, pb: 4 }}>
                <TableContainer component={Paper} elevation={0} sx={{
                    border: "1px solid #E2E8F0",
                    borderRadius: "8px"
                }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
                                {TABLE_HEADERS.map((header) => (
                                    <TableCell
                                        key={header.label}
                                        align={header.align}
                                        width={header.width}
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: "12px",
                                            color: "#64748B",
                                            borderBottom: "1px solid #E2E8F0",
                                            padding: "10px 16px",
                                            fontFamily: '"Heebo", "Roboto", sans-serif'
                                        }}
                                    >
                                        {header.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {schedules.map((schedule) => (
                                <TableRow
                                    key={schedule.id}
                                    sx={{
                                        "&:hover": { backgroundColor: "#FAFBFC" },
                                        opacity: schedule.isActive ? 1 : 0.6
                                    }}
                                >
                                    <TableCell align="right" sx={{ padding: "12px 16px" }}>
                                        <Typography sx={{
                                            fontSize: "14px",
                                            fontWeight: 600,
                                            color: "#1E293B",
                                            fontFamily: '"Heebo", "Roboto", sans-serif'
                                        }}>
                                            {schedule.name || "צ'ק-אין מתוזמן"}
                                        </Typography>
                                        <Typography sx={{ fontSize: "12px", color: "#94A3B8" }}>
                                            {schedule.scheduleTime}
                                        </Typography>
                                    </TableCell>

                                    <TableCell align="right" sx={{ padding: "12px 16px" }}>
                                        <Typography sx={{
                                            fontSize: "13px",
                                            color: "#475569",
                                            fontFamily: '"Heebo", "Roboto", sans-serif'
                                        }}>
                                            {formatScheduleDescription(schedule)}
                                        </Typography>
                                    </TableCell>

                                    <TableCell align="center" sx={{ padding: "12px 16px" }}>
                                        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                                            {(schedule.items || []).map((item, idx) => {
                                                const config = ITEM_TYPE_CONFIG[item.type];
                                                if (!config) return null;
                                                return (
                                                    <Tooltip key={idx} title={config.label}>
                                                        <Box sx={{
                                                            width: 28,
                                                            height: 28,
                                                            borderRadius: "6px",
                                                            backgroundColor: `${config.color}15`,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            "& svg": { color: config.color }
                                                        }}>
                                                            {config.icon}
                                                        </Box>
                                                    </Tooltip>
                                                );
                                            })}
                                        </Box>
                                    </TableCell>

                                    <TableCell align="center" sx={{ padding: "12px 16px" }}>
                                        <Typography sx={{
                                            fontSize: "13px",
                                            color: "#475569",
                                            fontFamily: '"Heebo", "Roboto", sans-serif'
                                        }}>
                                            {schedule.sendToAll
                                                ? "כולם"
                                                : schedule.targetTrainerName
                                                    ? `מתאמני ${schedule.targetTrainerName}`
                                                    : (schedule.targetTraineeCount || 0)}
                                        </Typography>
                                    </TableCell>

                                    <TableCell align="center" sx={{ padding: "12px 16px" }}>
                                        <Typography sx={{ fontSize: "13px", color: "#475569" }}>
                                            {formatDate(schedule.nextExecutionDate)}
                                        </Typography>
                                    </TableCell>

                                    <TableCell align="center" sx={{ padding: "12px 16px" }}>
                                        <Chip
                                            label={schedule.isActive ? "פעיל" : "מושהה"}
                                            size="small"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: "11px",
                                                fontFamily: '"Heebo", "Roboto", sans-serif',
                                                ...(schedule.isActive
                                                    ? { backgroundColor: "#D1FAE5", color: "#10B981" }
                                                    : { backgroundColor: "#FEF3C7", color: "#F59E0B" }),
                                                "& .MuiChip-label": { padding: "0 8px" }
                                            }}
                                        />
                                    </TableCell>

                                    <TableCell align="center" sx={{ padding: "12px 16px" }}>
                                        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                                            <Tooltip title={schedule.isActive ? "השהה" : "הפעל"}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleToggleActive(schedule)}
                                                    sx={{ color: schedule.isActive ? "#F59E0B" : "#10B981" }}
                                                >
                                                    {schedule.isActive ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="ערוך">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEdit(schedule)}
                                                    sx={{ color: "#64748B" }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="מחק">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDeleteClick(schedule)}
                                                    sx={{ color: "#DC2626" }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Mobile Cards */}
            <Box sx={{ display: { xs: "block", md: "none" }, px: 1, pb: 4 }}>
                {schedules.map((schedule) => (
                    <Paper
                        key={schedule.id}
                        variant="outlined"
                        sx={{
                            padding: "16px",
                            borderRadius: "8px",
                            marginBottom: "8px",
                            border: "1px solid #E2E8F0",
                            opacity: schedule.isActive ? 1 : 0.6
                        }}
                    >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
                            <Box>
                                <Typography sx={{
                                    fontSize: "15px",
                                    fontWeight: 600,
                                    color: "#1E293B",
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }}>
                                    {schedule.name || "צ'ק-אין מתוזמן"}
                                </Typography>
                                <Typography sx={{ fontSize: "13px", color: "#64748B", mt: 0.5 }}>
                                    {formatScheduleDescription(schedule)} | {schedule.scheduleTime}
                                </Typography>
                            </Box>
                            <Chip
                                label={schedule.isActive ? "פעיל" : "מושהה"}
                                size="small"
                                sx={{
                                    fontWeight: 600,
                                    fontSize: "11px",
                                    ...(schedule.isActive
                                        ? { backgroundColor: "#D1FAE5", color: "#10B981" }
                                        : { backgroundColor: "#FEF3C7", color: "#F59E0B" }),
                                    "& .MuiChip-label": { padding: "0 8px" }
                                }}
                            />
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                                {(schedule.items || []).map((item, idx) => {
                                    const config = ITEM_TYPE_CONFIG[item.type];
                                    if (!config) return null;
                                    return (
                                        <Box key={idx} sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: "4px",
                                            backgroundColor: `${config.color}15`,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            "& svg": { color: config.color, fontSize: "14px" }
                                        }}>
                                            {config.icon}
                                        </Box>
                                    );
                                })}
                            </Box>
                            <Typography sx={{ fontSize: "12px", color: "#94A3B8" }}>|</Typography>
                            <Typography sx={{ fontSize: "12px", color: "#64748B" }}>
                                {schedule.sendToAll
                                    ? "כל המתאמנים"
                                    : schedule.targetTrainerName
                                        ? `מתאמני ${schedule.targetTrainerName}`
                                        : `${schedule.targetTraineeCount || 0} מתאמנים`}
                            </Typography>
                        </Box>

                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography sx={{ fontSize: "12px", color: "#94A3B8" }}>
                                שליחה הבאה: {formatDate(schedule.nextExecutionDate)}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                                <IconButton size="small" onClick={() => handleToggleActive(schedule)}
                                    sx={{ color: schedule.isActive ? "#F59E0B" : "#10B981" }}>
                                    {schedule.isActive ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
                                </IconButton>
                                <IconButton size="small" onClick={() => handleEdit(schedule)} sx={{ color: "#64748B" }}>
                                    <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={() => handleDeleteClick(schedule)} sx={{ color: "#DC2626" }}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    </Paper>
                ))}
            </Box>

            {/* Dialogs */}
            <ScheduledCheckInDialog
                open={dialogOpen}
                onClose={() => { setDialogOpen(false); setEditData(null); }}
                onSubmit={handleSubmit}
                editData={editData}
            />

            {/* Delete Confirmation */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => { setDeleteDialogOpen(false); setDeleteTarget(null); }}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: "12px", direction: "rtl" } }}
            >
                <DialogTitle sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "16px 24px",
                    borderBottom: "1px solid #E2E8F0"
                }}>
                    <Typography sx={{
                        fontSize: "16px",
                        fontWeight: 600,
                        color: "#1E293B",
                        fontFamily: '"Heebo", "Roboto", sans-serif'
                    }}>
                        מחיקת צ'ק-אין מתוזמן
                    </Typography>
                    <IconButton
                        onClick={() => { setDeleteDialogOpen(false); setDeleteTarget(null); }}
                        size="small"
                        sx={{ color: "#64748B" }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ padding: "24px" }}>
                    <Typography sx={{
                        fontSize: "14px",
                        color: "#64748B",
                        fontFamily: '"Heebo", "Roboto", sans-serif'
                    }}>
                        האם אתה בטוח שברצונך למחוק את הצ'ק-אין המתוזמן "{deleteTarget?.name || "צ'ק-אין מתוזמן"}"?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ padding: "16px 24px", borderTop: "1px solid #E2E8F0", gap: 2 }}>
                    <Button
                        onClick={() => { setDeleteDialogOpen(false); setDeleteTarget(null); }}
                        variant="outlined"
                        sx={{
                            flex: 1,
                            borderColor: "#E2E8F0",
                            color: "#64748B",
                            fontFamily: '"Heebo", "Roboto", sans-serif',
                            "&:hover": { borderColor: "#CBD5E1", backgroundColor: "#F8FAFC" }
                        }}
                    >
                        ביטול
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        sx={{
                            flex: 1,
                            backgroundColor: "#DC2626",
                            fontFamily: '"Heebo", "Roboto", sans-serif',
                            "&:hover": { backgroundColor: "#B91C1C" }
                        }}
                    >
                        מחק
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ScheduledCheckInList;
